import { SettlementStateTag } from "@/components/partners";
import { client } from "@/hono";
import { useAgreementsData } from "@/hooks/use-agreements-data";
import { useAuth } from "@/hooks/use-auth";
import { AssignmentReferenceBookingsModal } from "@/modals/partners/assignment-reference-bookings-modal";
import { BlankSlateInline } from "@msuite/ui/blank-slate";
import { Button } from "@msuite/ui/button";
import { useDisclosure } from "@msuite/ui/hooks";
import { ErrorIcon, Icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { retryLimit, useQuery } from "@msuite/ui/query";
import { Spinner } from "@msuite/ui/spinner";
import { IconTag } from "@msuite/ui/tag";
import { responseHandler } from "@msuite/ui/utils";
import { guardArray } from "@msuite/utils/array";
import { formatNumber, getHoursString } from "@msuite/utils/numbers";
import { calculateBookings, getSettlementRecordState } from "@msuite/utils/partnerships";
import { guardString } from "@msuite/utils/strings";
import type { Tables } from "@msuite/utils/types";
import React from "react";
import { PartnerTimelineAssignmentReferenceApproveButton } from "./partner-timeline-assignment-reference-approve-button";

/** Props Interface */
interface AssignmentReferenceBookingsProps {
	referenceId: string;
	partnershipId: string;
	reference: Tables<"ticketAssignmentReferences">;
	date: string;
	hostOrgId: string;
}

export function AssignmentReferenceBookings({
	referenceId,
	partnershipId,
	date,
	reference,
	hostOrgId,
}: AssignmentReferenceBookingsProps) {
	/** Hooks */
	const { t } = useTranslation();
	const { organization } = useAuth();
	const assignmentReferenceBookingsModal = useDisclosure();

	/** Query */
	const { data, isLoading, isError, refetch } = useQuery({
		queryKey: ["partner", "assignment-reference-bookings", referenceId],
		queryFn: async () => {
			if (!organization) throw new Error("Organization not found");
			const response = await client.organizations[":orgId"].partnerships[
				":partnershipId"
			].timeline.reference[":referenceId"].bookings.$get({
				param: {
					orgId: guardString(organization?.id),
					partnershipId,
					referenceId,
				},
			});
			const data = await responseHandler(response);
			if (!data.success) throw new Error("Failed to fetch bookings");
			return guardArray(data.bookings);
		},
		retry: retryLimit(2),
	});

	/** Query */
	const { hourlyRate: hourlyAgreement } = useAgreementsData({ partnershipId, date });

	/** Guard */
	if (reference.calculationType !== "hourly") return null;

	/** Constants */
	const { submittedHours, adjustedHours, adjustedSettlement, hasBookings, hasChangedHours } =
		calculateBookings(data, hourlyAgreement);
	const isHost = organization?.id === hostOrgId;
	const state = getSettlementRecordState({
		isApproved: reference.isApproved,
		isReviewed: reference.isReviewed,
		hasBookings,
	});

	/** Guard */
	if (isLoading)
		return (
			<BlankSlateInline
				Icon={() => <Spinner />}
				label={t("general.loading")}
			/>
		);
	if (isError)
		return (
			<BlankSlateInline
				Icon={() => <ErrorIcon />}
				label={t("general.error")}
			/>
		);

	/** Render */
	return (
		<React.Fragment>
			<div className="col-start-1 col-span-2">
				{hasBookings ? (
					<div className="h-stack ">
						<div
							className="h-stack cursor-pointer"
							onClick={assignmentReferenceBookingsModal.onOpen}
						>
							<IconTag
								iconName="Clock"
								variant="default"
							>
								{getHoursString(submittedHours)}
							</IconTag>
							{hasChangedHours && (
								<IconTag
									iconName="SquarePen"
									variant="default"
								>
									{getHoursString(adjustedHours)}
								</IconTag>
							)}
							<Icon name="Equal" />
							<IconTag
								iconName="Euro"
								variant="default"
							>
								{formatNumber(adjustedSettlement, "currency")}
							</IconTag>
						</div>
						<div className="flex-1" />
						<SettlementStateTag state={state} />
						{isHost && (
							<PartnerTimelineAssignmentReferenceApproveButton
								reference={reference}
								partnershipId={partnershipId}
								hostOrgId={hostOrgId}
								hasBookings={hasBookings}
							/>
						)}
					</div>
				) : (
					// A record without hours names its state on the right like every other card; the
					// button on the left is the way to change it.
					<div className="h-stack">
						<Button
							onClick={assignmentReferenceBookingsModal.onOpen}
							icon={
								<Icon
									name="SquarePlus"
									className="stroke-primary-500 fill-primary-500/10"
								/>
							}
						>
							{t("partners.add_bookings")}
						</Button>
						<div className="flex-1" />
						<SettlementStateTag state={state} />
					</div>
				)}
			</div>
			{assignmentReferenceBookingsModal.isOpen && (
				<AssignmentReferenceBookingsModal
					isOpen={assignmentReferenceBookingsModal.isOpen}
					onClose={assignmentReferenceBookingsModal.onClose}
					referenceId={referenceId}
					partnershipId={partnershipId}
					date={date}
					onComplete={refetch}
				/>
			)}
		</React.Fragment>
	);
}
